(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const MOBILE_MQ = window.matchMedia("(max-width: 1000px)");

  const track = (eventName, parameters) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: "ga4Event",
      event_name: eventName,
      event_parameters: parameters
    });
  };
  const AUTO_MS = 3000;
  const SCROLL_RESUME_MS = 450;

  const STACK_STYLE = {
    front: {
      x: 0,
      y: 0,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      zIndex: 5,
      mediaOpacity: 1,
      overlayOpacity: 1
    },
    mid: {
      x: 5,
      y: 15,
      rotation: 1.5,
      scaleX: 0.95,
      scaleY: 1,
      opacity: 0.6,
      zIndex: 4,
      mediaOpacity: 0.05,
      overlayOpacity: 0
    },
    back: {
      x: 10,
      y: 29,
      rotation: 3,
      scaleX: 0.9167,
      scaleY: 1,
      opacity: 0.6,
      zIndex: 3,
      mediaOpacity: 0.05,
      overlayOpacity: 0
    },
    leaving: {
      x: 28,
      y: -18,
      rotation: 3,
      scaleX: 1,
      scaleY: 1,
      opacity: 0,
      zIndex: 6,
      mediaOpacity: 1,
      overlayOpacity: 0
    },
    hidden: {
      x: 10,
      y: 29,
      rotation: 3,
      scaleX: 0.9167,
      scaleY: 1,
      opacity: 0,
      zIndex: 2,
      mediaOpacity: 0.05,
      overlayOpacity: 0
    }
  };

  const STACK_CLASS = {
    front: "is-stack-front",
    mid: "is-stack-mid",
    back: "is-stack-back",
    hidden: "is-stack-hidden",
    leaving: "is-leaving"
  };

  const getStickyOffset = () => {
    if (window.si26Sticky?.getScrollOffset) {
      return window.si26Sticky.getScrollOffset();
    }
    const nav = document.querySelector("[data-si26-nav]");
    const header = document.querySelector(".site-header");
    const chrome = header ? Math.round(header.getBoundingClientRect().top + header.offsetHeight) : 0;
    if (nav) return Math.max(0, chrome + Math.round(nav.offsetHeight || 0));
    return Math.max(0, chrome);
  };

  const wrapIndex = (value, total) => {
    if (!total) return 0;
    return ((value % total) + total) % total;
  };

  const loadGsap = () =>
    new Promise((resolve) => {
      if (window.gsap) {
        resolve(window.gsap);
        return;
      }
      if (MOBILE_MQ.matches) {
        resolve(null);
        return;
      }
      if (window.__si26ProductsGsapRequested) {
        const wait = () => {
          if (window.gsap) resolve(window.gsap);
          else window.setTimeout(wait, 50);
        };
        wait();
        return;
      }
      window.__si26ProductsGsapRequested = true;
      const script = document.createElement("script");
      const timer = window.setTimeout(() => resolve(window.gsap || null), 1500);
      script.src = "https://cdn.jsdelivr.net/npm/gsap@3/dist/gsap.min.js";
      script.async = true;
      script.onload = () => {
        window.clearTimeout(timer);
        resolve(window.gsap || null);
      };
      script.onerror = () => {
        window.clearTimeout(timer);
        resolve(null);
      };
      document.head.appendChild(script);
    });

  const initProducts = async (root) => {
    if (!root || root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const pin = root.querySelector("[data-si26-pin]");
    const sticky = root.querySelector("[data-si26-sticky]");
    const tabs = [...root.querySelectorAll("[data-si26-scene-tab]")];
    const sceneDots = [...root.querySelectorAll("[data-si26-scene-dot]")];
    const scenes = [...root.querySelectorAll("[data-si26-scene]")];
    const dock = root.querySelector(".si26-products__dock");
    const dockItems = [...root.querySelectorAll("[data-si26-dock]")];

    if (!scenes.length) return;

    const sceneKeys = scenes.map((scene) => scene.dataset.si26Scene);
    const seenScenes = new Set();
    let sceneObserver = null;

    const trackSceneImpression = (scene) => {
      const key = scene?.dataset?.si26Scene;
      if (!key || seenScenes.has(key)) return;
      const rect = scene.getBoundingClientRect();
      const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      const height = rect.height || scene.offsetHeight || 1;
      if (visible < Math.min(height, window.innerHeight) * 0.35) return;
      seenScenes.add(key);
      track("module_impression", { module: `insta&shokz_${key}` });
    };

    const bindMobileSceneImpressions = () => {
      sceneObserver?.disconnect();
      sceneObserver = null;
      if (!MOBILE_MQ.matches || typeof IntersectionObserver === "undefined") return;
      sceneObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const key = entry.target.dataset.si26Scene;
            if (!key || seenScenes.has(key)) {
              sceneObserver.unobserve(entry.target);
              return;
            }
            seenScenes.add(key);
            track("module_impression", { module: `insta&shokz_${key}` });
            sceneObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.35 }
      );
      scenes.forEach((scene) => sceneObserver.observe(scene));
    };

    root.querySelectorAll(".si26-products__capsule").forEach((link) => {
      link.addEventListener("click", () => {
        const scene = link.closest("[data-si26-scene]");
        const key = scene?.dataset.si26Scene || "";
        const contentName = scene?.querySelector(".si26-products__scene-heading")?.textContent?.trim() || "";
        track("select_content", {
          content_type: `insta&shokz_${key}`,
          content_name: contentName,
          button_name: link.dataset.si26TrackButton || ""
        });
      });
    });
    const productsByScene = {};
    const productIndexByScene = {};

    scenes.forEach((scene) => {
      const key = scene.dataset.si26Scene;
      const list = [...scene.querySelectorAll("[data-si26-slide]")].map((slide, localIndex) => {
        const capsule = slide.querySelector(".si26-products__capsule");
        return {
          localIndex,
          slide,
          scene,
          sceneKey: key,
          href: capsule?.getAttribute("href") || "#",
          external: capsule?.getAttribute("target") === "_blank"
        };
      });
      productsByScene[key] = list;
      const activeLocal = list.findIndex((item) => item.slide.classList.contains("is-active"));
      productIndexByScene[key] = activeLocal >= 0 ? activeLocal : 0;
    });

    root.style.setProperty("--si26-auto-ms", `${AUTO_MS}ms`);

    let sceneIndex = 0;
    let autoTimer = null;
    let scrollResumeTimer = null;
    let userScrolling = false;
    let ticking = false;
    let lastPinScene = -1;
    let stackTimeline = null;
    let gsap = null;
    /** Product autoplay only after the section is reached for the first time. */
    let hasEntered = false;
    let autoAllowed = false;

    if (!MOBILE_MQ.matches && !reduceMotion.matches) {
      gsap = await loadGsap();
      if (gsap) root.classList.add("is-gsap-stack");
    }

    const useGsapStack = () => Boolean(gsap) && !MOBILE_MQ.matches && !reduceMotion.matches;

    const currentSceneKey = () => sceneKeys[sceneIndex] || sceneKeys[0];
    const currentProducts = () => productsByScene[currentSceneKey()] || [];
    const currentProductIndex = () => productIndexByScene[currentSceneKey()] || 0;

    const getMedia = (scene) => scene?.querySelector(".si26-products__media");
    const getOverlay = (scene) => scene?.querySelector(".si26-products__overlay");

    const clearStackClasses = (scene) => {
      Object.values(STACK_CLASS).forEach((name) => scene.classList.remove(name));
      scene.classList.remove("is-active");
    };

    const clearInlineState = (scene) => {
      if (!scene || !gsap) return;
      gsap.set(scene, {
        clearProps: "x,y,rotation,scale,scaleX,scaleY,opacity,transformOrigin,zIndex,pointerEvents"
      });
      const media = getMedia(scene);
      const overlay = getOverlay(scene);
      if (media) gsap.set(media, { clearProps: "opacity" });
      if (overlay) gsap.set(overlay, { clearProps: "opacity" });
    };

    const setStackClass = (scene, stateName) => {
      clearStackClasses(scene);
      scene.classList.add(STACK_CLASS[stateName] || STACK_CLASS.hidden);
      if (stateName === "front") scene.classList.add("is-active");
    };

    const setCardStateInstant = (scene, stateName) => {
      if (!scene) return;
      const style = STACK_STYLE[stateName] || STACK_STYLE.hidden;
      setStackClass(scene, stateName);

      if (!useGsapStack()) return;

      gsap.set(scene, {
        x: style.x,
        y: style.y,
        rotation: style.rotation,
        scaleX: style.scaleX,
        scaleY: style.scaleY,
        opacity: style.opacity,
        zIndex: style.zIndex,
        pointerEvents: stateName === "front" ? "auto" : "none",
        transformOrigin: "center center"
      });

      const media = getMedia(scene);
      const overlay = getOverlay(scene);
      if (media) gsap.set(media, { opacity: style.mediaOpacity });
      if (overlay) gsap.set(overlay, { opacity: style.overlayOpacity });
    };

    const tweenCardState = (timeline, scene, stateName, position = 0) => {
      if (!timeline || !scene || !useGsapStack()) return;
      const style = STACK_STYLE[stateName] || STACK_STYLE.hidden;
      setStackClass(scene, stateName);

      gsap.set(scene, {
        zIndex: style.zIndex,
        pointerEvents: stateName === "front" ? "auto" : "none",
        transformOrigin: "center center"
      });

      timeline.to(
        scene,
        {
          x: style.x,
          y: style.y,
          rotation: style.rotation,
          scaleX: style.scaleX,
          scaleY: style.scaleY,
          opacity: style.opacity,
          duration: 0.8,
          ease: "power1.out"
        },
        position
      );

      const media = getMedia(scene);
      const overlay = getOverlay(scene);
      if (media) {
        timeline.to(media, { opacity: style.mediaOpacity, duration: 0.6, ease: "power1.out" }, position + 0.1);
      }
      if (overlay) {
        timeline.to(
          overlay,
          {
            opacity: style.overlayOpacity,
            duration: 0.5,
            ease: "power1.out"
          },
          position + (stateName === "front" ? 0.2 : 0.1)
        );
      }
    };

    const applyDesktopStackLayout = (frontIndex) => {
      scenes.forEach((scene, i) => {
        const distance = wrapIndex(i - frontIndex, scenes.length);
        let state = "hidden";
        if (distance === 0) state = "front";
        else if (distance === 1) state = "mid";
        else if (distance === 2) state = "back";
        setCardStateInstant(scene, state);
      });
    };

    const getCircularScene = (i) => scenes[wrapIndex(i, scenes.length)];

    const animateDesktopStackTransition = (previousIndex, nextIndex) => {
      if (!useGsapStack()) return false;
      if (scenes.length < 2) return false;

      const forwardDist = wrapIndex(nextIndex - previousIndex, scenes.length);
      const backwardDist = wrapIndex(previousIndex - nextIndex, scenes.length);
      let direction = 0;
      if (forwardDist === 1) direction = 1;
      else if (backwardDist === 1) direction = -1;
      else return false;

      if (stackTimeline) {
        stackTimeline.kill();
        stackTimeline = null;
      }

      applyDesktopStackLayout(previousIndex);
      stackTimeline = gsap.timeline();

      if (direction > 0) {
        const leaving = getCircularScene(previousIndex);
        const promotedFront = getCircularScene(nextIndex);
        const promotedMid = getCircularScene(nextIndex + 1);
        const incomingBack = getCircularScene(nextIndex + 2);

        tweenCardState(stackTimeline, leaving, "leaving", 0);

        if (promotedFront) {
          gsap.set(promotedFront, {
            zIndex: STACK_STYLE.front.zIndex,
            pointerEvents: "auto",
            transformOrigin: "center center"
          });
          stackTimeline.to(
            promotedFront,
            {
              x: 0,
              y: 0,
              rotation: 0,
              scaleX: 1,
              scaleY: 1,
              opacity: 1,
              duration: 0.8,
              ease: "power1.out"
            },
            0
          );
          setStackClass(promotedFront, "front");

          const frontMedia = getMedia(promotedFront);
          const frontOverlay = getOverlay(promotedFront);
          if (frontMedia) {
            stackTimeline.to(frontMedia, { opacity: 1, duration: 0.6, ease: "power1.out" }, 0.1);
          }
          if (frontOverlay) {
            stackTimeline.to(frontOverlay, { opacity: 1, duration: 0.5, ease: "power1.out" }, 0.2);
          }
        }

        tweenCardState(stackTimeline, promotedMid, "mid", 0);

        if (incomingBack && scenes.length > 3) {
          const hiddenStyle = STACK_STYLE.hidden;
          const backStyle = STACK_STYLE.back;
          const incomingMedia = getMedia(incomingBack);
          const incomingOverlay = getOverlay(incomingBack);

          gsap.set(incomingBack, {
            zIndex: backStyle.zIndex,
            pointerEvents: "none",
            transformOrigin: "center center"
          });
          if (incomingMedia) gsap.set(incomingMedia, { opacity: backStyle.mediaOpacity });
          if (incomingOverlay) gsap.set(incomingOverlay, { opacity: 0 });
          setStackClass(incomingBack, "back");

          stackTimeline.fromTo(
            incomingBack,
            {
              x: hiddenStyle.x,
              y: hiddenStyle.y,
              rotation: hiddenStyle.rotation,
              scaleX: hiddenStyle.scaleX,
              scaleY: hiddenStyle.scaleY,
              opacity: 0
            },
            {
              x: backStyle.x,
              y: backStyle.y,
              rotation: backStyle.rotation,
              scaleX: backStyle.scaleX,
              scaleY: backStyle.scaleY,
              opacity: backStyle.opacity,
              duration: 0.8,
              ease: "power1.out"
            },
            0
          );
        }
      } else {
        const incomingFront = getCircularScene(nextIndex);
        const demotedMid = getCircularScene(previousIndex);
        const demotedBack = getCircularScene(previousIndex + 1);
        const demotedHidden = getCircularScene(previousIndex + 2);

        if (incomingFront) {
          setCardStateInstant(incomingFront, "hidden");
          tweenCardState(stackTimeline, incomingFront, "front", 0);
        }
        tweenCardState(stackTimeline, demotedMid, "mid", 0);
        tweenCardState(stackTimeline, demotedBack, "back", 0);
        if (scenes.length > 3) tweenCardState(stackTimeline, demotedHidden, "hidden", 0);
      }

      stackTimeline.eventCallback("onComplete", () => {
        applyDesktopStackLayout(nextIndex);
        stackTimeline = null;
      });

      return true;
    };

    const syncDockVisibility = () => {
      if (!dock) return;
      if (!MOBILE_MQ.matches) {
        dock.classList.remove("is-inview");
        dock.setAttribute("aria-hidden", "true");
        return;
      }
      const show = scenes.some((scene) => {
        const rect = scene.getBoundingClientRect();
        const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        const height = rect.height || scene.offsetHeight || 1;
        return visible >= height * 0.2;
      });
      dock.classList.toggle("is-inview", show);
      dock.setAttribute("aria-hidden", show ? "false" : "true");
    };

    const setDockActive = (key) => {
      dockItems.forEach((item) => {
        item.classList.toggle("is-active", item.dataset.si26Dock === key);
      });
    };

    const setTabsActive = (key) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.si26SceneTab === key;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
      });
      sceneDots.forEach((dot) => {
        const active = dot.dataset.si26SceneDot === key;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-selected", active ? "true" : "false");
      });
      const scenePosition = sceneKeys.indexOf(key);
      const scenePrev = root.querySelector("[data-si26-scene-prev]");
      const sceneNext = root.querySelector("[data-si26-scene-next]");
      if (scenePrev) scenePrev.disabled = scenePosition <= 0;
      if (sceneNext) sceneNext.disabled = scenePosition < 0 || scenePosition >= sceneKeys.length - 1;
      setDockActive(key);
    };

    /** Product carousel within one scene — fade only, never touches stack. */
    const showProduct = (sceneKey, localIndex) => {
      const list = productsByScene[sceneKey] || [];
      if (!list.length) return;
      const nextLocal = wrapIndex(localIndex, list.length);
      productIndexByScene[sceneKey] = nextLocal;
      const item = list[nextLocal];
      const scene = item.scene;

      list.forEach((entry, i) => {
        const active = i === nextLocal;
        entry.slide.classList.toggle("is-active", active);
        if (active) entry.slide.removeAttribute("hidden");
        else entry.slide.setAttribute("hidden", "");
      });

      const stage = scene?.querySelector("[data-si26-carousel]");
      const segments = [...(stage?.querySelectorAll("[data-si26-progress-seg]") || [])];
      segments.forEach((seg, i) => {
        const active = i === nextLocal;
        seg.classList.toggle("is-active", active);
        seg.setAttribute("aria-selected", active ? "true" : "false");
      });

      const bgImages = [...(scene?.querySelectorAll("[data-si26-bg]") || [])];
      bgImages.forEach((img) => {
        img.classList.toggle("is-active", Number(img.dataset.si26Bg) === nextLocal);
        img.classList.remove("is-swap-out");
      });

      if (autoTimer && sceneKey === currentSceneKey()) playProgress(scene);

      const hit = scene?.querySelector("[data-si26-hit]");
      if (hit) {
        hit.setAttribute("href", item.href || "#");
        if (item.external) {
          hit.setAttribute("target", "_blank");
          hit.setAttribute("rel", "noopener noreferrer");
        } else {
          hit.removeAttribute("target");
          hit.removeAttribute("rel");
        }
      }
    };

    const clearProgressFill = () => {
      root.querySelectorAll(".si26-products__progress-seg.is-filling").forEach((seg) => {
        seg.classList.remove("is-filling");
      });
    };

    const playProgress = (scene) => {
      clearProgressFill();
      if (!autoTimer || !scene) return;
      const active = scene.querySelector(".si26-products__progress-seg.is-active");
      if (!active) return;
      active.classList.remove("is-filling");
      void active.offsetWidth;
      active.classList.add("is-filling");
    };

    const stopAuto = () => {
      if (autoTimer) {
        window.clearInterval(autoTimer);
        autoTimer = null;
      }
      clearProgressFill();
    };

    const startAuto = () => {
      stopAuto();
      if (!autoAllowed || !hasEntered) return;
      if (reduceMotion.matches || userScrolling) return;
      const list = currentProducts();
      if (list.length < 2) return;

      autoTimer = window.setInterval(() => {
        const key = currentSceneKey();
        showProduct(key, currentProductIndex() + 1);
      }, AUTO_MS);
      const scene = scenes.find((item) => item.dataset.si26Scene === currentSceneKey());
      playProgress(scene);
    };

    const markEntered = () => {
      if (hasEntered) return;
      hasEntered = true;
      autoAllowed = true;
      startAuto();
    };

    /** Scene stack only — keeps each scene's product index. */
    const goToScene = (nextSceneIndex, { animate = true } = {}) => {
      const prevSceneIndex = sceneIndex;
      sceneIndex = wrapIndex(nextSceneIndex, sceneKeys.length);
      const key = sceneKeys[sceneIndex];
      setTabsActive(key);
      // Reveal that scene's current product without resetting it.
      showProduct(key, productIndexByScene[key] || 0);

      if (MOBILE_MQ.matches) {
        scenes.forEach((scene) => {
          clearStackClasses(scene);
          clearInlineState(scene);
          scene.classList.add("is-active");
        });
        return;
      }

      if (!animate || reduceMotion.matches || prevSceneIndex === sceneIndex) {
        if (stackTimeline) {
          stackTimeline.kill();
          stackTimeline = null;
        }
        applyDesktopStackLayout(sceneIndex);
        return;
      }

      if (!animateDesktopStackTransition(prevSceneIndex, sceneIndex)) {
        applyDesktopStackLayout(sceneIndex);
      }
    };

    const syncPin = () => {
      const offset = getStickyOffset();
      if (sticky) {
        sticky.style.top = `${offset}px`;
        sticky.style.setProperty("--si26-pin-offset", `${offset}px`);
      }
      if (!pin || MOBILE_MQ.matches) return;

      const range = Math.max(1, pin.offsetHeight - sticky.offsetHeight);
      const scrolled = Math.min(range, Math.max(0, offset - pin.getBoundingClientRect().top));
      const progress = scrolled / range;
      const nextScene = Math.min(sceneKeys.length - 1, Math.floor(progress * sceneKeys.length));

      if (nextScene !== lastPinScene) {
        lastPinScene = nextScene;
        goToScene(nextScene);
      }
      trackSceneImpression(scenes[sceneIndex]);
    };

    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        syncDockVisibility();
        if (!MOBILE_MQ.matches && pin) {
          const offset = getStickyOffset();
          const rect = pin.getBoundingClientRect();
          const pinned = rect.top <= offset + 1 && rect.bottom > offset + sticky.offsetHeight * 0.5;

          if (pinned) {
            markEntered();
            const wasScrolling = userScrolling;
            userScrolling = true;
            stopAuto();

            const range = Math.max(1, pin.offsetHeight - sticky.offsetHeight);
            const scrolled = Math.min(range, Math.max(0, offset - rect.top));
            const nextScene = Math.min(
              sceneKeys.length - 1,
              Math.floor((scrolled / range) * sceneKeys.length)
            );

            if (!wasScrolling || nextScene !== lastPinScene) {
              goToScene(nextScene);
            }
            lastPinScene = nextScene;

            window.clearTimeout(scrollResumeTimer);
            scrollResumeTimer = window.setTimeout(() => {
              userScrolling = false;
              startAuto();
            }, SCROLL_RESUME_MS);
          }

          if (sticky) {
            sticky.style.top = `${offset}px`;
            sticky.style.setProperty("--si26-pin-offset", `${offset}px`);
          }
        }
        ticking = false;
      });
    };

    const scrollPinToScene = (targetSceneIndex) => {
      if (!pin || !sticky || MOBILE_MQ.matches) return;
      const offset = getStickyOffset();
      const range = Math.max(1, pin.offsetHeight - sticky.offsetHeight);
      const top =
        window.scrollY +
        pin.getBoundingClientRect().top -
        offset +
        (targetSceneIndex / sceneKeys.length) * range +
        1;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: reduceMotion.matches ? "auto" : "smooth"
      });
    };

    const jumpToScene = (tabIndex) => {
      const index = wrapIndex(tabIndex, sceneKeys.length);
      if (MOBILE_MQ.matches) {
        goToScene(index, { animate: false });
        const target = scenes[index];
        if (target) {
          const top = window.scrollY + target.getBoundingClientRect().top - getStickyOffset() + 1;
          window.scrollTo({
            top: Math.max(0, top),
            behavior: reduceMotion.matches ? "auto" : "smooth"
          });
        }
        return;
      }
      userScrolling = true;
      stopAuto();
      scrollPinToScene(index);
      window.clearTimeout(scrollResumeTimer);
      scrollResumeTimer = window.setTimeout(() => {
        userScrolling = false;
        startAuto();
      }, SCROLL_RESUME_MS);
    };

    tabs.forEach((tab, tabIndex) => {
      tab.addEventListener("click", () => {
        jumpToScene(tabIndex);
      });
    });

    sceneDots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const key = dot.dataset.si26SceneDot;
        jumpToScene(Math.max(0, sceneKeys.indexOf(key)));
      });
    });

    root.querySelector("[data-si26-scene-prev]")?.addEventListener("click", () => {
      if (sceneIndex <= 0) return;
      jumpToScene(sceneIndex - 1);
    });
    root.querySelector("[data-si26-scene-next]")?.addEventListener("click", () => {
      if (sceneIndex >= sceneKeys.length - 1) return;
      jumpToScene(sceneIndex + 1);
    });

    dockItems.forEach((item, dockIndex) => {
      item.addEventListener("click", (event) => {
        event.preventDefault();
        const key = item.dataset.si26Dock;
        const tabIndex = Math.max(0, sceneKeys.indexOf(key));
        goToScene(tabIndex, { animate: false });
        if (MOBILE_MQ.matches) {
          const target = scenes[tabIndex] || scenes.find((scene) => scene.dataset.si26Scene === key);
          if (target) {
            const top = window.scrollY + target.getBoundingClientRect().top - getStickyOffset() + 1;
            window.scrollTo({
              top: Math.max(0, top),
              behavior: reduceMotion.matches ? "auto" : "smooth"
            });
          }
        } else {
          scrollPinToScene(dockIndex >= 0 ? tabIndex : dockIndex);
        }
      });
    });

    root.querySelectorAll("[data-si26-carousel]").forEach((stage) => {
      const scene = stage.closest("[data-si26-scene]");
      const sceneKey = scene?.dataset.si26Scene;
      const prev = stage.querySelector("[data-si26-prev]");
      const next = stage.querySelector("[data-si26-next]");
      const segments = [...stage.querySelectorAll("[data-si26-progress-seg]")];

      const restartAutoAfterManual = () => {
        stopAuto();
        startAuto();
      };

      prev?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        // Arrows only ever step products inside this scene.
        if (sceneKey !== currentSceneKey() && !MOBILE_MQ.matches) return;
        showProduct(sceneKey, (productIndexByScene[sceneKey] || 0) - 1);
        restartAutoAfterManual();
      });

      next?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (sceneKey !== currentSceneKey() && !MOBILE_MQ.matches) return;
        showProduct(sceneKey, (productIndexByScene[sceneKey] || 0) + 1);
        restartAutoAfterManual();
      });

      segments.forEach((seg) => {
        seg.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          const local = Number(seg.dataset.si26ProgressSeg);
          if (!Number.isFinite(local)) return;
          if (sceneKey !== currentSceneKey() && !MOBILE_MQ.matches) return;
          showProduct(sceneKey, local);
          restartAutoAfterManual();
        });
      });
    });

    root.querySelectorAll("[data-si26-carousel]").forEach((stage) => {
      stage.addEventListener("pointerdown", (event) => {
        // Arrow / progress / capsule clicks should not trigger the stage press scale.
        if (event.target.closest(".si26-products__controls, .si26-products__capsule, a, button")) {
          return;
        }
        stage.classList.add("is-pressed");
      });
      stage.addEventListener("pointerup", () => stage.classList.remove("is-pressed"));
      stage.addEventListener("pointerleave", () => stage.classList.remove("is-pressed"));
      stage.addEventListener("pointercancel", () => stage.classList.remove("is-pressed"));
    });

    if (typeof IntersectionObserver !== "undefined") {
      // First time the products block enters view → enable product autoplay.
      const enterSpy = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            markEntered();
            enterSpy.disconnect();
          }
        },
        { threshold: 0.35, rootMargin: "0px 0px -10% 0px" }
      );
      enterSpy.observe(sticky || root);

      const spy = new IntersectionObserver(
        (entries) => {
          if (!MOBILE_MQ.matches) return;
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible?.target) return;
          const key = visible.target.dataset.si26Scene;
          const idx = sceneKeys.indexOf(key);
          if (idx >= 0) {
            const sceneChanged = idx !== sceneIndex;
            sceneIndex = idx;
            setTabsActive(key);
            if (sceneChanged && autoTimer) startAuto();
          }
        },
        {
          rootMargin: "-20% 0px -45% 0px",
          threshold: [0.15, 0.35, 0.55]
        }
      );
      scenes.forEach((scene) => spy.observe(scene));

      if (dock && scenes.length) {
        const expand = new IntersectionObserver(
          (entries) => {
            if (!MOBILE_MQ.matches) return;
            if (entries.some((entry) => entry.isIntersecting)) {
              dock.classList.add("is-expanded");
            }
          },
          { threshold: 0.28 }
        );
        expand.observe(scenes[0]);

        const dockSpy = new IntersectionObserver(
          () => {
            syncDockVisibility();
          },
          { threshold: [0, 0.2, 0.5] }
        );
        scenes.forEach((scene) => dockSpy.observe(scene));
      }
    }

    const onModeChange = () => {
      stopAuto();
      if (stackTimeline) {
        stackTimeline.kill();
        stackTimeline = null;
      }
      lastPinScene = -1;
      userScrolling = false;

      syncDockVisibility();

      bindMobileSceneImpressions();

      if (MOBILE_MQ.matches) {
        root.classList.remove("is-gsap-stack");
        scenes.forEach((scene) => {
          clearInlineState(scene);
          clearStackClasses(scene);
          scene.classList.add("is-active");
          const key = scene.dataset.si26Scene;
          showProduct(key, productIndexByScene[key] || 0);
        });
        // Do not autoplay until the section has been entered.
        if (hasEntered) startAuto();
        return;
      }

      if (gsap) root.classList.add("is-gsap-stack");
      goToScene(sceneIndex, { animate: false });
      if (hasEntered) startAuto();
      syncPin();
    };

    if (typeof MOBILE_MQ.addEventListener === "function") {
      MOBILE_MQ.addEventListener("change", onModeChange);
    } else if (typeof MOBILE_MQ.addListener === "function") {
      MOBILE_MQ.addListener(onModeChange);
    }

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    // Initial layout only — no autoplay until first enter.
    sceneKeys.forEach((key) => showProduct(key, productIndexByScene[key] || 0));
    onModeChange();
  };

  const init = (scope = document) => {
    if (scope.matches?.("[data-si26-products]")) {
      initProducts(scope);
    }
    scope.querySelectorAll?.("[data-si26-products]").forEach(initProducts);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
